import { FC, useEffect, useState } from 'react';
import { Grid, Skeleton, SkeletonProps } from '@mui/material';
import { CartesianGrid, Line, LineChart, ResponsiveContainer } from 'recharts';

const StyledSkeleton = ({children, style, ...rest}: SkeletonProps) => {
  const [height, setHeight] = useState(Math.floor(Math.random() * 200) + 100 );
  
  useEffect(() => {
    const i = setInterval(() => {
      setHeight(Math.floor(Math.random() * 200) + 100 );
    }, 2000);

    return () => clearInterval(i);
  }, []);

  return <Skeleton {...rest} style={{...style, transition: "height 1s", height: height}}>{children}</Skeleton>;
}

const LineChartSkeleton = () => {
  const [data, setData] = useState(Array.from(Array(24)).map((_, i) => ({
    name: i,
    uv: Math.floor(Math.random() * 1000) + 500,
    pv: Math.floor(Math.random() * 1000) + 200,
  })));

  useEffect(() => {
    const i = setInterval(() => {
      setData(Array.from(Array(24)).map((_, i) => ({
        name: i,
        uv: Math.floor(Math.random() * 1000) + 500,
        pv: Math.floor(Math.random() * 1000) + 200,
      })));
    }, 2000);

    return () => clearInterval(i);
  }, []);

  return (
    <div style={{ height: "410px", width: "100%" }}>
      <Skeleton 
        variant="rectangular" 
        animation="wave" 
        sx={{ 
          height: '100%', 
          width: '100%', 
          borderRadius: 2,
          opacity: 0.1,
          background: 'linear-gradient(90deg, transparent, rgba(0,0,0,0.04), transparent)'
        }} 
      />
      <div style={{ position: 'relative', top: '-410px', height: '410px', width: '100%', padding: '20px' }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={data}
          >
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eee" />
            <Line 
              type="monotone" 
              dataKey="uv" 
              stroke="#ddd" 
              strokeWidth={3} 
              dot={false} 
              animationDuration={1500}
            />
            <Line 
              type="monotone" 
              dataKey="pv" 
              stroke="#eee" 
              strokeWidth={3} 
              dot={false} 
              animationDuration={1500}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export type ChartSkeletonProps = {
  type: "columns";
  columns?: number;
} | {
  type: "line";
}

const ChartSkeleton: FC<ChartSkeletonProps> = (props) => {

  return (
    <div style={{height: "525px", width: "100%"}}>
      {props.type === "columns" && (
        <Grid container justifyContent='space-evenly' alignItems='flex-end' wrap="nowrap" style={{height: "410px"}}>
          {Array.from(Array(props.columns ?? 32)).map((el, idx) => {
            return <Grid item key={idx}> 
                <StyledSkeleton variant='rectangular' animation={false} style={{width: `${(1.25*32)/(props.columns ?? 32)}vw`}} />
              </Grid>
            })
          }
      </Grid>
      )}
      {props.type === "line" && (
        <LineChartSkeleton />
      )}
      <div style={{marginTop: "25px", paddingLeft: "1.25vw", paddingRight: "1.25vw"}}>
        <Skeleton variant='rectangular' animation={false} height={35}/>
      </div>
      <Grid container spacing={1} justifyContent="center" wrap="nowrap" style={{marginTop: "10px"}}>
        <Grid item>
          <Skeleton variant='rectangular' animation={false} height={20} width={75}/>
        </Grid>
        <Grid item>
          <Skeleton variant='rectangular' animation={false} height={20} width={75}/>
        </Grid>
        <Grid item>
          <Skeleton variant='rectangular' animation={false} height={20} width={75}/>
        </Grid>
      </Grid>
    </div>
  )
}

export default ChartSkeleton;